import { httpClient } from "../http-client";
import { ApiResponse } from "../../types/responses/api-response";

// ===== NOTIFICATIONS API =====

export interface Notification {
  id: string;
  userId: string;
  type: "info" | "success" | "warning" | "error" | "event" | "task" | "system";
  title: string;
  message: string;
  isRead: boolean;
  isImportant: boolean;
  data?: Record<string, any>;
  actionUrl?: string;
  actionText?: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationSettings {
  id: string;
  userId: string;
  email: {
    enabled: boolean;
    types: string[];
    frequency: "immediate" | "daily" | "weekly";
  };
  push: {
    enabled: boolean;
    types: string[];
  };
  sms: {
    enabled: boolean;
    types: string[];
  };
  inApp: {
    enabled: boolean;
    types: string[];
    sound: boolean;
    desktop: boolean;
  };
  quietHours: {
    enabled: boolean;
    startTime: string;
    endTime: string;
    timezone: string;
  };
}

export interface NotificationTemplate {
  id: string;
  name: string;
  type: string;
  subject: string;
  message: string;
  variables: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNotificationTemplateRequest {
  name: string;
  type: string;
  subject: string;
  message: string;
  variables?: string[];
}

export interface UpdateNotificationTemplateRequest {
  name?: string;
  type?: string;
  subject?: string;
  message?: string;
  variables?: string[];
  isActive?: boolean;
}

export const notificationsApi = {
  // ===== УПРАВЛЕНИЕ УВЕДОМЛЕНИЯМИ =====

  // Список уведомлений
  getNotifications: async (params?: {
    page?: number;
    limit?: number;
    isRead?: boolean;
    type?: string;
    isImportant?: boolean;
    status?: string[];
  }): Promise<ApiResponse<Notification[]>> => {
    // Правильное формирование параметров для массива status
    const queryParams: Record<string, any> = { ...params };
    if (params?.status) {
      delete queryParams["status"];
      params.status.forEach((s, index) => {
        queryParams[`status[${index}]`] = s;
      });
    }
    return httpClient.get("/notifications", { params: queryParams });
  },

  // Получение уведомления по ID
  getNotification: async (id: string): Promise<ApiResponse<Notification>> => {
    return httpClient.get(`/notifications/${id}`);
  },

  // Отметить как прочитанное
  // ВАЖНО: Backend использует POST, не PATCH
  markAsRead: async (id: string): Promise<ApiResponse<Notification>> => {
    return httpClient.post(`/notifications/${id}/read`);
  },

  // Отметить как непрочитанное (Backend: PATCH /notifications/:id с isRead: false)
  markAsUnread: async (id: string): Promise<ApiResponse<Notification>> => {
    return httpClient.patch(`/notifications/${id}`, { isRead: false });
  },

  // Удаление уведомления
  deleteNotification: async (
    id: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.delete(`/notifications/${id}`);
  },

  // Обновить произвольные поля уведомления
  patchNotification: async (
    id: string,
    data: Record<string, unknown>,
  ): Promise<ApiResponse<Notification>> => {
    return httpClient.patch(`/notifications/${id}`, data);
  },

  // Отметить конкретное уведомление как starred
  starNotification: async (
    id: string,
    isStarred: boolean,
  ): Promise<ApiResponse<Notification>> => {
    return httpClient.patch(`/notifications/${id}/star`, { isStarred });
  },

  // Архивировать уведомление
  archiveNotification: async (
    id: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.patch(`/notifications/${id}/archive`);
  },

  // Пометить все уведомления как прочитанные
  markAllAsRead: async (): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.patch("/notifications/read-all");
  },

  // Очистить все уведомления
  // ВАЖНО: Backend использует /notifications без суффикса
  clearAllNotifications: async (): Promise<
    ApiResponse<{ message: string }>
  > => {
    return httpClient.delete("/notifications");
  },

  // ===== НАСТРОЙКИ УВЕДОМЛЕНИЙ =====

  // Настройки уведомлений
  getNotificationSettings: async (): Promise<
    ApiResponse<NotificationSettings>
  > => {
    return httpClient.get("/notifications/settings");
  },

  // Обновление настроек
  updateNotificationSettings: async (
    settings: Partial<NotificationSettings>,
  ): Promise<ApiResponse<NotificationSettings>> => {
    return httpClient.patch("/notifications/settings", settings);
  },

  // W1-S5 (NTF-001): sendTestNotification удалён — BE-роут POST /notifications/test
  // принадлежал V2-контроллеру, который удалён (V1 канон). Тест push-уведомлений —
  // push.api.ts sendTest (POST /notifications/push/test).

  // ===== ШАБЛОНЫ УВЕДОМЛЕНИЙ =====

  // Шаблоны уведомлений
  getNotificationTemplates: async (params?: {
    page?: number;
    limit?: number;
    type?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<NotificationTemplate[]>> => {
    return httpClient.get("/notifications/templates", { params });
  },

  // Создание шаблона
  createNotificationTemplate: async (
    data: CreateNotificationTemplateRequest,
  ): Promise<ApiResponse<NotificationTemplate>> => {
    return httpClient.post("/notifications/templates", data);
  },

  // Получение шаблона по ID
  getNotificationTemplate: async (
    id: string,
  ): Promise<ApiResponse<NotificationTemplate>> => {
    return httpClient.get(`/notifications/templates/${id}`);
  },

  // Обновление шаблона
  updateNotificationTemplate: async (
    id: string,
    data: UpdateNotificationTemplateRequest,
  ): Promise<ApiResponse<NotificationTemplate>> => {
    return httpClient.patch(`/notifications/templates/${id}`, data);
  },

  // Удаление шаблона
  deleteNotificationTemplate: async (
    id: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.delete(`/notifications/templates/${id}`);
  },

  // ===== КАТЕГОРИИ УВЕДОМЛЕНИЙ =====
  getCategories: async (): Promise<
    ApiResponse<
      Array<{
        id: string;
        key: string;
        name: string;
        description?: string;
        isActive: boolean;
      }>
    >
  > => {
    return httpClient.get("/notifications/categories");
  },
  createCategory: async (data: {
    key: string;
    name: string;
    description?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<{ id: string }>> => {
    return httpClient.post("/notifications/categories", data);
  },
  updateCategory: async (
    id: string,
    data: {
      key?: string;
      name?: string;
      description?: string;
      isActive?: boolean;
    },
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.patch(`/notifications/categories/${id}`, data);
  },
  deleteCategory: async (
    id: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.delete(`/notifications/categories/${id}`);
  },

  // ===== PUSH ПОДПИСКА =====

  registerPushSubscription: async (data: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    userAgent?: string;
  }): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.post("/notifications/push/subscribe", data);
  },

  // Backend: DELETE с body { endpoint }
  unregisterPushSubscription: async (
    endpoint: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.delete("/notifications/push/unsubscribe", {
      data: { endpoint },
    });
  },

  // XS-009 (Волна 7): getWebhooks/createWebhook/deleteWebhook удалены —
  // дублировали fake notifications/webhook/endpoints; рабочая webhooks-подсистема
  // живёт в services/api/webhooks.api.ts (/webhook-subscriptions, AD-416).

  // ===== BULK ОПЕРАЦИИ =====
  sendBulkNotifications: async (payload: {
    audience: {
      userIds?: string[];
      roles?: string[];
      filters?: Record<string, any>;
    };
    templateId?: string;
    override?: {
      title?: string;
      message?: string;
      data?: Record<string, any>;
      categoryKey?: string;
      type?: string;
      isImportant?: boolean;
      actionUrl?: string;
      actionText?: string;
    };
    channels?: Array<"inApp" | "push" | "email" | "sms">;
    schedule?: { when: string } | null;
    dryRun?: boolean;
  }): Promise<ApiResponse<{ jobId: string; accepted: number }>> => {
    return httpClient.post("/notifications/bulk-send", payload);
  },
  // NOT_IMPLEMENTED_BACKEND: getBulkStatus может вернуть 404; в UI обрабатывать 404 или не вызывать до появления API.
  getBulkStatus: async (
    jobId: string,
  ): Promise<
    ApiResponse<{
      jobId: string;
      status: "queued" | "running" | "completed" | "failed";
      processed: number;
      total: number;
    }>
  > => {
    return httpClient.get(`/notifications/bulk/${jobId}`);
  },
};

export default notificationsApi;
